(() => {
  if (customElements.get('st-stock-label')) return;
  const config = document.getElementById('st-stock-config');
  let token;
  try { token = JSON.parse(config?.textContent || '{}').token; } catch { return; }
  if (!token) return;

  const localLocation = 'gid://shopify/Location/64038830244';
  const supplierLocation = 'gid://shopify/Location/99794452644';
  const pending = new Map();
  const cache = new Map();
  let scheduled = false;

  function classify(node, tracked) {
    if (!node || typeof node.availableForSale !== 'boolean') return null;
    if (!node.availableForSale) return 'oos';
    if (!tracked || !Array.isArray(node.storeAvailability?.nodes)) return null;
    const stocked = node.storeAvailability.nodes.filter(stock => stock?.available === true &&
      Number.isInteger(stock.quantityAvailable) && stock.quantityAvailable > 0);
    if (stocked.some(stock => stock.location?.id === localLocation)) return 'local';
    if (stocked.some(stock => stock.location?.id === supplierLocation)) return 'supplier';
    return null;
  }

  async function flush() {
    scheduled = false;
    const jobs = [...pending.entries()];
    pending.clear();
    for (let offset = 0; offset < jobs.length; offset += 40) {
      const batch = jobs.slice(offset, offset + 40);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      let nodes = [];
      try {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST', signal: controller.signal,
          headers: {'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token},
          body: JSON.stringify({
            query: 'query StockLabels($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id availableForSale storeAvailability(first:20){nodes{available quantityAvailable location{id}}}}}}',
            variables: {ids: batch.map(([id]) => `gid://shopify/ProductVariant/${id}`)},
          }),
        });
        if (!response.ok) throw Error('Stock lookup failed');
        const result = await response.json();
        if (result.errors || !Array.isArray(result.data?.nodes)) throw Error('Incomplete stock lookup');
        nodes = result.data.nodes;
      } catch {
        // Keep server-rendered labels when location evidence is unavailable.
      } finally { clearTimeout(timeout); }
      for (const [id, resolves] of batch) {
        const node = nodes.find(value => value?.id === `gid://shopify/ProductVariant/${id}`) || null;
        if (node) cache.set(id, {node, expires: Date.now() + 30000});
        resolves.forEach(resolve => resolve(node));
      }
    }
  }

  function lookup(id) {
    const cached = cache.get(id);
    if (cached?.expires > Date.now()) return Promise.resolve(cached.node);
    return new Promise(resolve => {
      if (!pending.has(id)) pending.set(id, []);
      pending.get(id).push(resolve);
      if (!scheduled) { scheduled = true; queueMicrotask(flush); }
    });
  }

  class StockLabel extends HTMLElement {
    connectedCallback() {
      this.originalState ||= this.dataset.stockState;
      this.originalTitle ||= this.querySelector('.supplier-badge__title')?.textContent;
      this.originalNote ||= this.querySelector('.supplier-badge__note')?.textContent;
      this.refresh();
    }
    disconnectedCallback() { this.request = (this.request || 0) + 1; }
    async refresh() {
      const request = this.request = (this.request || 0) + 1;
      const id = this.dataset.variantId;
      if (!/^\d+$/.test(id || '') || this.dataset.available !== 'true') return;
      this.apply(this.originalState, this.originalTitle, this.originalNote);
      const node = await lookup(id);
      if (!this.isConnected || request !== this.request || this.dataset.variantId !== id) return;
      const state = classify(node, this.dataset.tracked === 'true');
      if (state === 'local') this.apply(state, this.dataset.context === 'pdp' ? 'In store now' : 'In stock', this.dataset.context === 'pdp' ? 'Ships Australia-wide, or collect from our Mildura shop.' : '');
      if (state === 'supplier') this.apply(state, 'Available to order', this.dataset.context === 'pdp' ? 'We order this in for you. Ask us about timing.' : '');
      if (state === 'oos') this.apply(state, 'Out of stock', '');
    }
    apply(state, title, note) {
      this.dataset.stockState = state;
      for (const name of ['instock','order','oos','local','supplier']) this.classList.toggle(`supplier-badge--${name}`, state === name);
      const titleNode = this.querySelector('.supplier-badge__title');
      const noteNode = this.querySelector('.supplier-badge__note');
      if (titleNode) titleNode.textContent = title;
      if (noteNode) noteNode.textContent = note || '';
      this.title = note || title;
    }
  }
  customElements.define('st-stock-label', StockLabel);
  addEventListener('pageshow', event => {
    if (!event.persisted) return;
    cache.clear();
    document.querySelectorAll('st-stock-label').forEach(label => label.refresh());
  });
})();
